import type { ReactNode } from 'react'
import { useCallback, useMemo, useState } from 'react'

import type { DefaultFilterSelectProps, SelectCapitalizeProps } from '@/types/components/select'
import { cn } from '@/utils/cn'
import type { PopoverProps } from '@radix-ui/react-popover'
import { AccordionContent, AccordionTrigger } from '../common/accordion'
import { Button, type ButtonProps } from '../common/button'
import { Popover, PopoverTrigger } from '../common/popover'
import { ArrowIcon } from '../icons/arrow'
import { FilterSelectContent } from './filterSelectContent'

interface FilterSelectTriggerContentProps {
  renderTriggerContent?: (selectedItems: string[]) => ReactNode
}

type FilterSelectProps = DefaultFilterSelectProps & SelectCapitalizeProps & Pick<PopoverProps, 'modal'>

export const FilterSelect = ({
  items,
  className,
  scrollClassName,
  modal,
  ...props
}: FilterSelectProps & FilterSelectTriggerContentProps) => {
  const [open, setOpen] = useState(false)
  const [selectedItemsSnapshot, setSelectedItemsSnapshot] = useState<string[]>([])
  const [shouldSort, setShouldSort] = useState(false)

  const sortedItems = useMemo(() => {
    if (!shouldSort) return items

    const selected = items.filter((item) => selectedItemsSnapshot.includes(item))
    const unselected = items.filter((item) => !selectedItemsSnapshot.includes(item))
    return [...selected, ...unselected]
  }, [items, selectedItemsSnapshot, shouldSort])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open)
      if (open) {
        setSelectedItemsSnapshot([...props.selectedItems])
        setShouldSort(props.selectedItems.length > 0)
      }
    },
    [props.selectedItems]
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={modal}>
      <FilterSelectTrigger className={className}>
        <div className="flex w-full max-w-full items-center gap-2 truncate">
          <SelectTriggerContent {...props} />
        </div>
      </FilterSelectTrigger>
      <FilterSelectContent items={sortedItems} scrollClassName={scrollClassName} modal={modal} {...props} />
    </Popover>
  )
}

const SelectTriggerContent = (
  props: Omit<FilterSelectProps, 'items' | 'setSelectedItems'> & SelectCapitalizeProps & FilterSelectTriggerContentProps
): ReactNode => {
  const { selectedItems, name, renderItemPrefix, capitalize, itemFormatter, renderTriggerContent } = props
  const itemsLength = selectedItems.length

  if (itemsLength === 0) return <span className="first-letter:uppercase">{name}</span>

  if (renderTriggerContent) return <>{renderTriggerContent(selectedItems)}</>

  if (itemsLength > 1)
    return (
      <span className={cn(capitalize && 'text-body-foreground first-letter:uppercase')}>{itemsLength} selected</span>
    )

  const firstItem = selectedItems[0]
  const formattedFirstItem = itemFormatter ? itemFormatter(firstItem) : firstItem

  return (
    <>
      {renderItemPrefix?.(firstItem)}
      <span className={cn(capitalize && 'text-body-foreground first-letter:uppercase')}>{formattedFirstItem}</span>
    </>
  )
}

export const FilterAccordionContent = ({ children }: { children: ReactNode }) => (
  <AccordionContent className="rounded-xl bg-surface">{children}</AccordionContent>
)

export const FilterAccordionTrigger = ({ name }: { name: string }) => (
  <AccordionTrigger className="group flex w-full items-center justify-between py-2 font-medium text-heading-foreground text-sm outline-none">
    <span className="first-letter:uppercase">{name}</span>
    <ArrowIcon direction="down" size={20} className="group-data-[state=open]:-scale-y-100 transition-transform" />
  </AccordionTrigger>
)

export const FilterSelectTrigger = ({ className, children }: ButtonProps) => {
  return (
    <PopoverTrigger asChild>
      <Button
        variant="default"
        size="md"
        className={cn(
          'group flex h-10 items-center gap-1 rounded-xl border bg-surface pr-2 pl-3 text-left transition-colors',
          'hocus:border-accent hocus:text-body-foreground',
          className
        )}
      >
        {children}
        <div className="group-data-[state=open]:-scale-y-100 transition-transform">
          <ArrowIcon direction="down" size={20} />
        </div>
      </Button>
    </PopoverTrigger>
  )
}
